import { API } from '../constants';
import { backendClient } from './backendClient';
import axios from 'axios';

export type MessageType = 'FREEZE_REQUEST' | 'INFO_REQUEST' | 'ALERT' | 'GENERAL';
export type MessageStatus = 'OPEN' | 'ACTION_TAKEN';

export interface Message {
  id: number;
  senderId?: number;
  senderUsername?: string;
  senderRole?: string;
  senderAddress?: string;
  targetType: 'USER' | 'ROLE' | 'ALL';
  targetValue?: string | null;
  recipientRole?: string | null;
  title: string;
  content?: string;
  createdAt: string;
  read: boolean;
  readAt?: string | null;
  messageType: MessageType;
  payload?: Record<string, any>;
  status?: MessageStatus | string;
  actionType?: string | null;
  actionNote?: string | null;
  actionTakenAt?: string | null;
  actionTakenBy?: string | null;
}

export interface SendMessageRequest {
  senderUsername: string;
  title: string;
  content?: string;
  targetType?: 'USER' | 'ROLE' | 'ALL';
  targetValue?: string;
  recipientRole?: string;
  messageType?: MessageType;
  payload?: Record<string, any>;
}

export interface MessageActionRequest {
  username: string;
  actionType: string;
  actionNote?: string;
  payload?: Record<string, any>;
}

const BASE = API.MESSAGES;
const client = backendClient.getClient();

export const messageService = {
  async getMessages(username: string, options?: { status?: 'all' | 'unread'; role?: string }): Promise<Message[]> {
    const res = await client.get(BASE, {
      params: {
        username,
        status: options?.status,
        role: options?.role,
      },
    });
    return res.data || [];
  },

  async getMessagesByRole(role: string, options?: { status?: 'all' | 'unread' }): Promise<Message[]> {
    const res = await client.get(BASE, {
      params: {
        role,
        status: options?.status,
      },
    });
    return res.data || [];
  },

  async getSentMessages(username: string): Promise<Message[]> {
    const res = await client.get(`${BASE}/sent`, {
      params: { username },
    });
    return res.data || [];
  },

  async getUnreadCount(username: string): Promise<number> {
    const res = await client.get(`${BASE}/unread-count`, {
      params: { username },
    });
    return typeof res.data === 'number'
      ? res.data
      : (res.data?.unreadCount ?? res.data?.count ?? 0);
  },

  async markAsRead(messageId: number, username: string): Promise<void> {
    try {
      await client.put(`${BASE}/${messageId}/read`, null, {
        params: { username },
      });
    } catch (error) {
      if (!axios.isAxiosError(error) || error.response?.status !== 404) {
        throw error;
      }
      await client.post(`${BASE}/read/${messageId}`, null, {
        params: { username },
      });
    }
  },

  async recordAction(messageId: number, data: MessageActionRequest): Promise<Message> {
    try {
      const res = await client.put(`${BASE}/${messageId}/action`, data);
      return res.data;
    } catch (error) {
      if (!axios.isAxiosError(error) || error.response?.status !== 404) {
        throw error;
      }
      await client.post(`${BASE}/read/${messageId}`, null, {
        params: { username: data.username },
      });
      return {
        id: messageId,
        targetType: 'USER',
        title: '',
        createdAt: new Date().toISOString(),
        read: true,
        messageType: 'GENERAL',
      };
    }
  },

  async sendMessage(data: SendMessageRequest): Promise<Message | void> {
    const res = await client.post(BASE, data);
    return res.data?.data;
  },
};

export default messageService;
